<template>
  <div class="background" data-testid="pricing-page">
    <!-- Loading State -->
    <v-container v-if="loading" class="text-center py-12 fill-height">
      <v-row align="center" justify="center">
        <v-col>
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <p class="mt-4">Loading pricing options...</p>
        </v-col>
      </v-row>
    </v-container>

    <!-- Main Content -->
    <div v-else>
      <!-- Header Section -->
      <v-container class="pricing-header-container">
        <v-row justify="center" align="center">
          <v-col cols="12">
            <div class="text-center py-12">
              <img
                src="@/assets/logo/logo.png"
                alt="ThreadFlow Logo"
                class="logo mb-6"
              />
              <h1 class="pricing-title" data-testid="pricing-title">
                Simple Pricing. Endless Possibilities for Custom
                Pop-Up Stores and Product Sales
              </h1>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- Pricing Cards -->
      <v-container class="pricing-cards-container">
        <v-row justify="center" data-testid="pricing-cards">
          <v-col
            v-for="(subscription, index) in activeSubscriptions"
            :key="subscription.id"
            cols="12"
            sm="6"
            md="4"
            class="px-3 mb-4"
          >
            <!-- The plan's stored accent paints the header band (and the
                 featured card's border) through --plan-accent. -->
            <v-card
              :class="['pricing-card', 'hover', { 'highlighted': index === 1 }]"
              elevation="3"
              class="d-flex flex-column"
              height="100%"
              :style="{ '--plan-accent': accentHex(subscription) }"
              :data-testid="`pricing-plan-card-${subscription.id}`"
            >
              <v-card-title class="pricing-card-title justify-center">
                <h2>{{ subscription.title || subscription.name }}</h2>
              </v-card-title>

              <v-card-text class="text-center flex-grow-1 d-flex flex-column">
                <!-- Icon: the plan's stored icon + accent (never its position). -->
                <div class="pricing-icon-container">
                  <PlanIcon
                    :plan="subscription"
                    :size="80"
                    :test-id="`pricing-plan-icon-${subscription.id}`"
                  />
                </div>

                <!-- Description -->
                <PlanDescription
                  class="pricing-description"
                  :test-id="`pricing-plan-description-${subscription.id}`"
                  :description="subscription.description"
                  :fallback="getDescription(subscription, index)"
                />

                <!-- Price: every nonzero part (flat monthly fee and/or
                     per-active-store price), via the shared helper. -->
                <div class="pricing-price-section mt-auto">
                  <div class="pricing-amount" :data-testid="`pricing-plan-price-${subscription.id}`">
                    {{ priceLine(subscription) }}
                  </div>
                </div>
              </v-card-text>

              <v-card-actions class="pa-4">
                <v-btn
                  block
                  size="large"
                  :color="index === 1 ? 'primary' : 'grey-darken-1'"
                  :variant="index !== 1 ? 'outlined' : 'elevated'"
                  theme="dark"
                  class="start-trial-btn"
                  :data-testid="`pricing-plan-start-${subscription.id}`"
                  @click="selectSubscription(subscription)"
                >
                  Start Trial
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { planPriceLine } from "@/utils/planPrice";
import PlanDescription from "@/components/Pricing/PlanDescription.vue";
import PlanIcon from "@/components/Pricing/PlanIcon.vue";
import { resolvePlanPresentation } from "@/utils/planPresentation";

export default {
  name: "PricingPage",
  components: { PlanDescription, PlanIcon },
  data() {
    return {
      loading: true,
      activeSubscriptions: [],
    };
  },

  async mounted() {
    await this.loadSubscriptions();
  },

  methods: {
    async loadSubscriptions() {
      try {
        this.loading = true;
        const response = await this.$axios.get("/auth/getActiveSubscriptions");
        this.activeSubscriptions = response.data || [];

        // Order is the Super Admin's plan order from the API
        // (pricing_plan.sort_order); do not re-sort here.

        this.loading = false;
      } catch (err) {
        console.error("Error loading subscriptions:", describeError(err));
        this.$store.dispatch("raiseError", "Failed to load pricing options");
        this.loading = false;
      }
    },

    selectSubscription(subscription) {
      // Navigate to signup with the subscription name
      const subscriptionName = subscription.name || subscription.title.toLowerCase().replace(/\s+/g, '-');
      this.$router.push({ name: 'sign-up', params: { id: subscriptionName } });
    },

    accentHex(subscription) {
      return resolvePlanPresentation(subscription).accentHex;
    },

    priceLine(subscription) {
      return planPriceLine({
        monthlyCents: subscription.monthly_price_cents,
        perActiveCents: subscription.per_active_store_cents,
      });
    },

    getDescription(subscription, index) {
      // Default descriptions based on tier
      const descriptions = [
        "For individual sales reps or teams that want a flexible, yet powerful tool to build stores and grow business online.",
        "For individual sales reps or teams experiencing growth in online sales.",
        "For sales reps or teams scaling their online business to the next level."
      ];
      return descriptions[index] || "Powerful tools to manage your online sales.";
    }
  },
};
</script>

<style scoped>
.background {
  min-height: 100vh;
  background-color: #f5f8fa;
}

.logo {
  max-height: 80px; max-width: 150px;
}

.pricing-header-container {
  max-width: 1200px;
}

.pricing-title {
  font-size: 2rem;
  font-weight: normal;
  line-height: 1.3;
  text-wrap: balance;
  color: #212121;
  margin: 0;
}

.pricing-cards-container {
  max-width: 1200px;
  padding: 20px;
}

.pricing-card {
  border-radius: 10px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: white;
}

.pricing-card.hover:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
  z-index: 2;
}

.pricing-card.highlighted {
  border: 3px solid var(--plan-accent, #119ae9);
  transform: scale(1.05);
}

.pricing-card.highlighted:hover {
  transform: scale(1.05) translateY(-8px);
}

/* Header band in the plan's accent. Every allow-listed accent clears 3:1
   against white, the WCAG AA bar for this large bold title. */
.pricing-card-title {
  padding: 24px 16px;
  background: var(--plan-accent, #119ae9);
  color: white;
  border-radius: 10px 10px 0 0;
}

.pricing-card-title h2 {
  font-size: 1.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: white;
  margin: 0;
}

.pricing-icon-container {
  padding: 30px 0 20px;
}

.pricing-description {
  font-size: 1rem;
  color: #424242;
  line-height: 1.6;
  padding: 0 16px;
  min-height: 100px;
  margin-bottom: 20px;
}

.pricing-price-section {
  padding: 20px 0;
}

.pricing-amount {
  font-size: 1.9rem;
  font-weight: 700;
  color: #212121;
  line-height: 1.25;
}

.start-trial-btn {
  font-weight: 600;
  border-radius: 25px;
}

@media (max-width: 960px) {
  .pricing-title {
    font-size: 1.6rem;
  }

  .pricing-card.highlighted {
    transform: scale(1);
  }

  .pricing-card.highlighted:hover {
    transform: translateY(-8px);
  }

  .pricing-amount {
    font-size: 1.7rem;
  }
}

@media (max-width: 600px) {
  .pricing-title {
    font-size: 1.5rem;
  }

  .pricing-amount {
    font-size: 1.5rem;
  }

  .pricing-description {
    min-height: 80px;
  }
}
</style>


